import {
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { Dispatch, SetStateAction, useState, useEffect, useRef } from 'react';

import { PaginationModel } from '@eng-mono/atlassian/jira/mojira-bugtracker/util-issue-list-fe';

const paginationResets = new Set<string>();

interface IssueRowPaginationProps {
  currIssueIndex: number;
  setCurrIssueIndex: Dispatch<SetStateAction<number>>;
  paginationModel: PaginationModel;
  setPaginationModel: (model: PaginationModel) => void;
  rowCount: number;
  pageSize: number;
  hasNextPage?: boolean;
  knownTotalPages?: number;
  loading?: boolean;
}

export function IssueRowPagination({
  currIssueIndex,
  setCurrIssueIndex,
  paginationModel,
  setPaginationModel,
  rowCount,
  pageSize,
  hasNextPage = false,
  knownTotalPages = 0,
  loading = false,
}: IssueRowPaginationProps) {
  const [showResetMessage, setShowResetMessage] = useState(false);
  const lastPageRef = useRef(0);
  const paginationKey = `issue-pagination-${Date.now()}`;

  const page = paginationModel?.page ?? 0;
  const _currentIssueNumber = page * pageSize + currIssueIndex + 1;

  const _estimatedTotal = Math.max(
    rowCount || 0,
    (knownTotalPages || 0) * pageSize || rowCount || 0
  );

  useEffect(() => {
    if (
      lastPageRef.current > 0 &&
      page === 0 &&
      !paginationResets.has(paginationKey)
    ) {
      setShowResetMessage(true);
      paginationResets.add(paginationKey);

      setTimeout(() => {
        setShowResetMessage(false);
      }, 5000);

      setTimeout(() => {
        paginationResets.delete(paginationKey);
      }, 60000);
    }

    lastPageRef.current = page;
  }, [page, paginationKey]);

  const _handlePrevIssue = () => {
    if (currIssueIndex > 0) {
      setCurrIssueIndex(currIssueIndex - 1);
    } else if (page > 0) {
      setPaginationModel({
        ...paginationModel,
        page: page - 1,
      });
      setCurrIssueIndex(pageSize - 1);
    }
  };

  const _handleNextIssue = () => {
    if (currIssueIndex < pageSize - 1) {
      setCurrIssueIndex(currIssueIndex + 1);
    } else if (hasNextPage) {
      setPaginationModel({
        ...paginationModel,
        page: page + 1,
      });
      setCurrIssueIndex(0);
    }
  };

  const _handlePrevPage = () => {
    if (page > 0) {
      setPaginationModel({
        ...paginationModel,
        page: page - 1,
      });
      setCurrIssueIndex(0);
    }
  };

  const _handleNextPage = () => {
    if (hasNextPage) {
      setPaginationModel({
        ...paginationModel,
        page: page + 1,
      });
      setCurrIssueIndex(0);
    }
  };

  const _isFirstIssue = page === 0 && currIssueIndex === 0;
  const _isLastIssue = !hasNextPage && currIssueIndex === pageSize - 1;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
    >
      {showResetMessage && (
        <Typography
          variant="caption"
          color="info.main"
          sx={{
            marginBottom: '8px',
            textAlign: 'center',
          }}
          aria-live="polite"
        >
          Your pagination session has expired. You've been returned to the first
          page.
        </Typography>
      )}
    </div>
  );
}
